/** @file createClient: the typed Pose3D client — auth, content, `projects.*` and `puppets.*` (W6). */
import { PRODUCTION_URL } from '../constants/productionUrl';
import type { AuthMethods } from './authMethods';
import { authMethods } from './authMethods';
import type { CheckMethods } from './checkMethods';
import { checkMethods } from './checkMethods';
import type { ClientOptions } from './clientOptions';
import type { CommandMethods } from './commandMethods';
import { commandMethods } from './commandMethods';
import type { LiveHandlers, LiveSession } from './connectLive';
import { connectLive } from './connectLive';
import type { ContentMethods } from './contentMethods';
import { contentMethods } from './contentMethods';
import type { ExportMethods } from './exportMethods';
import { exportMethods } from './exportMethods';
import type { ImageMethods } from './imageMethods';
import { imageMethods } from './imageMethods';
import type { ProjectMethods } from './projectMethods';
import { projectMethods } from './projectMethods';
import type { PuppetMethods } from './puppetMethods';
import { puppetMethods } from './puppetMethods';
import type { RenderMethods } from './renderMethods';
import { renderMethods } from './renderMethods';
import { request } from './request';
import type { SlotMethods } from './slotMethods';
import { slotMethods } from './slotMethods';

/** Everything about one puppet: list/create/get/trash/restore/purge, then every per-puppet call by puppet id. */
export type PuppetClient = PuppetMethods & CommandMethods & Omit<RenderMethods, 'fetchRenderImages'> & ExportMethods & ImageMethods
  & SlotMethods & CheckMethods & {
    /** A WebSocket live session on one puppet (`connectLive`). */
    live: (puppetId: string, actor: string, handlers: LiveHandlers, sinceRevision?: number) => Promise<LiveSession>;
  };

/** Every client member; `url` is the resolved base URL. */
export type Pose3DClient = AuthMethods & ContentMethods & Pick<RenderMethods, 'fetchRenderImages'> & {
  url: string;
  /** `GET /api/v1/health`. */
  health: () => Promise<{ ok: true; version: string; build?: { sha: string; at: string } }>;
  /** Projects (games): collections of puppets with shared defaults and one export. */
  projects: ProjectMethods;
  /** Puppets (one character, enemy, weapon or prop each) and every call that acts on one. */
  puppets: PuppetClient;
};

/** A client for `opts.url` (default `PRODUCTION_URL`, trailing slashes stripped) with a token or a session cookie. */
export function createClient(opts: ClientOptions = {}): Pose3DClient {
  const url = (opts.url ?? PRODUCTION_URL).replace(/\/+$/, '');
  const o: ClientOptions = { ...opts, url };
  const { fetchRenderImages, ...render } = renderMethods(o);
  return {
    url,
    health: async () => (await request<{ ok: true; version: string; build?: { sha: string; at: string } }>(o, { method: 'GET', path: '/api/v1/health', accept: 'json' })).value,
    ...authMethods(o),
    ...contentMethods(o),
    fetchRenderImages,
    projects: projectMethods(o),
    puppets: {
      ...puppetMethods(o), ...commandMethods(o), ...render, ...exportMethods(o), ...imageMethods(o), ...slotMethods(o), ...checkMethods(o),
      live: (puppetId, actor, handlers, sinceRevision) => connectLive(o, puppetId, actor, handlers, sinceRevision),
    },
  };
}
